import { useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./Settings.module.scss";
import { usePageSubtitle } from "@/hooks/usePageSubtitle";
import SettingsNavigation from "./components/SettingsNavigation/SettingsNavigation";
import GeneralSettings from "./components/GeneralSettings/GeneralSettings";
import NotificationChannels from "./components/NotificationChannels/NotificationChannels";
import type { NotificationChannelsHandle } from "./components/NotificationChannels/NotificationChannels";
import PaymentAndTax from "./components/PaymentAndTax/PaymentAndTax";
import type { PaymentAndTaxHandle } from "./components/PaymentAndTax/PaymentAndTax";
import DataAndPrivacy from "./components/DataAndPrivacy/DataAndPrivacy";
import SettingsBottomBar from "./components/SettingsBottomBar/SettingsBottomBar";
import toast from "@/lib/toast";

const SUBTITLE_BY_SECTION: Record<string, string> = {
  general: "Club configuration, app behaviour and integrations",
  notifications: "Push, email and SMS notification preferences",
  app_experience: "App experience, branding and member view preferences",
  payments_tax: "Tax rates, currency, settlements and invoicing",
  data_privacy: "Data retention, privacy policies and export preferences",
  danger_zone: "Danger zone and irreversible club actions",
};

const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = searchParams.get("section") || searchParams.get("tab") || "notifications";

  const notificationChannelsRef = useRef<NotificationChannelsHandle>(null);
  const paymentAndTaxRef = useRef<PaymentAndTaxHandle>(null);
  const [isSavingNotificationChannels, setIsSavingNotificationChannels] = useState(false);
  const [isSavingPaymentTax, setIsSavingPaymentTax] = useState(false);

  const handleSelectSection = (id: string) => {
    setSearchParams({ section: id });
  };

  const subtitle =
    SUBTITLE_BY_SECTION[activeSection] ??
    "Club configuration, app behaviour and integrations";

  usePageSubtitle(subtitle);

  const handleSave = () => {
    if (activeSection === "notifications") {
      notificationChannelsRef.current?.save();
      return;
    }
    if (activeSection === "payments_tax") {
      paymentAndTaxRef.current?.save();
      return;
    }
    toast.success("Settings saved successfully");
  };

  const handleCancel = () => {
    if (activeSection === "notifications") {
      notificationChannelsRef.current?.cancel();
      return;
    }
    if (activeSection === "payments_tax") {
      paymentAndTaxRef.current?.cancel();
      return;
    }
    toast.warning("Changes discarded");
  };

  return (
    <div className={styles.settingsPage}>
      <div className={styles.layout}>
        <aside className={styles.navigationColumn}>
          <SettingsNavigation
            activeId={activeSection}
            onSelect={handleSelectSection}
          />
        </aside>

        <main className={styles.contentColumn}>
          {/* {activeSection === "general" && <GeneralSettings />} */}
          {activeSection === "notifications" && (
            <NotificationChannels
              ref={notificationChannelsRef}
              onSavingChange={setIsSavingNotificationChannels}
            />
          )}
          {activeSection === "payments_tax" && (
            <PaymentAndTax ref={paymentAndTaxRef} onSavingChange={setIsSavingPaymentTax} />
          )}
          {activeSection === "data_privacy" && <DataAndPrivacy />}
          {activeSection !== "general" &&
            activeSection !== "notifications" &&
            activeSection !== "payments_tax" &&
            activeSection !== "data_privacy" && <GeneralSettings />}
        </main>
      </div>

      <SettingsBottomBar
        onSave={handleSave}
        onCancel={handleCancel}
        isSaving={
          (activeSection === "notifications" && isSavingNotificationChannels) ||
          (activeSection === "payments_tax" && isSavingPaymentTax)
        }
      />
    </div>
  );
};

export default Settings;
